import { initFederation as nativeInitFederation } from '@angular-architects/native-federation';
import { initCacheTagRemotes  } from './app/federation.utils';
import { environment } from './environments/environment';
import { setWLDefaultDomains } from './app/core/config/white-label.constants';
import { WhiteLabelingUtils } from './app/core/services/white-labeling.utils';

// Tell the index.html boot guard the entry bundle actually executed. If a static chunk is
// missing/blocked, main.js's module graph fails and this line never runs — under es-module-shims
// the failure can surface as a promise rejection rather than a <script> error event, so this flag
// is the reliable signal that lets the guard detect a failed bundle and recover.
(window as any).__el_app_started = true;

const DEFAULT_APP_NAME = 'Everleagues';
// Timeout for the pre-bootstrap white-labeling calls: a hung API (e.g. mid-deployment)
// must not block the whole app boot indefinitely.
const WL_FETCH_TIMEOUT_MS = 8000;

if (environment.production) {
  // NOTE: enableProdMode() lives in bootstrap.ts, not here.
  // main.ts is the native-federation entry and runs before the import map
  // is initialized, so a static import from '@angular/core' would fail with
  // "Unable to resolve specifier '@angular/core'".

  if(window){
    window.console.log = window.console.time = 
    window.console.timeEnd = window.console.info = 
    window.console.error = window.console.debug = function(){
      // Don't log anything.
    };
  }
}

async function fetchDefaultDomains(excludeLocalhost: boolean = false): Promise<string[] | null> {
  try {
      const apiUrl = environment.apiUrl;
      const response = await fetch(apiUrl + '/sys/wl-default-domains', { signal: AbortSignal.timeout(WL_FETCH_TIMEOUT_MS) });
      if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
              let domains = data.map(d => d.trim()).filter(d => d.length > 0);
              
              // Optionally exclude localhost for testing white-labeling
              if (excludeLocalhost) {
                  domains = domains.filter(d => d !== 'localhost' && d !== '127.0.0.1');
              }
              
              if (domains.length > 0) {
                  setWLDefaultDomains(domains);
                  return domains;
              }
          }
          console.error('Invalid white label domains data received from API');
      } else {
          console.error('Failed to fetch white label domains, API returned status:', response.status);
      }
  } catch (e) {
      console.error('Failed to fetch default domains from API:', e);
  }
  return null;
}

function applyBranding(title: string, iconUrl: string) {
  if (title) document.title = title;
  if (iconUrl) {
      let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
      if (!link) {
          link = document.createElement('link');
          link.type = 'image/x-icon';
          link.rel = 'icon';
          document.head.appendChild(link);
      }
      link.href = iconUrl;
  }
}

async function initializeWhiteLabeling(): Promise<void> {
  try {
      // Check URL param to test white-labeling locally: add ?testWL=true to exclude localhost
      const urlParams = new URLSearchParams(window.location.search);
      const testWhiteLabeling = urlParams.get('testWL') === 'true';
      
      // Fetch default domains from API (Azure Key Vault)
      const WL_DEFAULT_DOMAINS = await fetchDefaultDomains(testWhiteLabeling);

      const hostname = window.location.hostname;
      // If API failed (null), treat all everleagues.com domains as default (safe fallback)
      const isDefault = WL_DEFAULT_DOMAINS ? WL_DEFAULT_DOMAINS.indexOf(hostname) > -1 : true;

      function handleDomainNotFound() {
          // @ts-ignore
          window.el_wl_loading = false;
          WhiteLabelingUtils.clearBranding();
          // Use hardcoded string here as translations aren't loaded yet
          // AppTitleStrategy will update with proper translation after Angular boots
          applyBranding(DEFAULT_APP_NAME, 'assets/favicon-16x16.png');

          if (window.location.pathname !== '/domain-not-found') {
              history.pushState(null, '', '/domain-not-found');
          }
      }

      // Check if hostname is in the default domains list (fetched from Azure Key Vault)
      // This allows testing white-labeling by removing localhost from the list
      if (isDefault) {
          // Default domain (including localhost if in list) - use default branding
          applyBranding(DEFAULT_APP_NAME, 'assets/favicon-16x16.png');
      } else if (!hostname.endsWith('.everleagues.com') && hostname !== 'everleagues.com' && hostname !== 'localhost' && hostname !== '127.0.0.1') {
          // Non-everleagues.com and non-localhost domain - skip white labeling checks
          applyBranding(DEFAULT_APP_NAME, 'assets/favicon-16x16.png');
      } else {
          // Custom everleagues.com subdomain - check for white labeling
          // @ts-ignore
          window.el_wl_loading = true;

          WhiteLabelingUtils.clearBranding();

          const apiUrl = environment.apiUrl;
          const publicStorageLink = environment.publicStorageLink;
          
          let code = hostname.split('.')[0];

          try {
              const response = await fetch(apiUrl + '/v2/org/whitelabeling/check/' + code, { signal: AbortSignal.timeout(WL_FETCH_TIMEOUT_MS) });
              if (response.ok) {
                  const data = await response.json();
                  if (data && data !== false) {
                      const orgId = data;
                      try {
                          const settingsResponse = await fetch(apiUrl + '/v2/org/' + orgId + '/whitelabeling', { signal: AbortSignal.timeout(WL_FETCH_TIMEOUT_MS) });
                          if (settingsResponse.ok) {
                              const settings = await settingsResponse.json();
                            //   console.warn('WL Settings Fetched', settings);
                              
                              const brandingBase = publicStorageLink + 'brandings/' + code;
                              const config = {
                                  orgId: settings.orgId,
                                  orgName: settings.orgName,
                                  whiteLabelingEnabled: settings.whiteLabelingEnabled,
                                  defaultLanguage: settings.defaultLanguage,
                                  primaryLogoUrl: brandingBase + '/primary-logo.png',
                                  secondaryLogoUrl: brandingBase + '/secondary-logo.png',
                                  faviconUrl: brandingBase + '/favicon.ico',
                                  languageFileUrl: brandingBase + '/langs/'
                              };

                              WhiteLabelingUtils.saveToStorage(config);
                              applyBranding(config.orgName, config.faviconUrl);
                          } else {
                              console.error('WL Settings fetch failed, loading with default branding. Status:', settingsResponse.status);
                              applyBranding(DEFAULT_APP_NAME, 'assets/favicon-16x16.png');
                          }
                      } catch (e) {
                          console.error('WL Settings Parse Error, loading with default branding', e);
                          applyBranding(DEFAULT_APP_NAME, 'assets/favicon-16x16.png');
                      } finally {
                          // @ts-ignore
                          window.el_wl_loading = false;
                      }
                  } else {
                      handleDomainNotFound();
                  }
              } else {
                  // Any error from the WL check (4xx/5xx, e.g. API restarting mid-deployment) means
                  // WL is unavailable, not that the domain is invalid — exit WL and load normally.
                  // Only an authoritative `false` answer above means the subdomain has no WL org.
                  console.error('WL check failed, loading without white-labeling. Status:', response.status);
                  // @ts-ignore
                  window.el_wl_loading = false;
                  applyBranding(DEFAULT_APP_NAME, 'assets/favicon-16x16.png');
              }
          } catch (e) {
              // Failed to communicate with backend — skip white-labeling and load as normal
              console.error('WL Check communication failure, loading without white-labeling', e);
              // @ts-ignore
              window.el_wl_loading = false;
              applyBranding(DEFAULT_APP_NAME, 'assets/favicon-16x16.png');
          }
      }
  } catch (e) {
      // Any unhandled WL exception falls back to a normal default-branded load
      console.error('WL Init Error, loading without white-labeling', e);
      applyBranding(DEFAULT_APP_NAME, 'assets/favicon-16x16.png');
  } finally {
      // Never leave the flag set — app init (loadBranding) polls it and would wait forever
      // @ts-ignore
      window.el_wl_loading = false;
  }
}

// Boot failure recovery (reload-with-counter, watchdog, error panel) lives in the index.html
// boot guard so it survives a failure of this very bundle. Route any failure caught here to it.
function recoverFromBootFailure(err: unknown): void {
  console.error('[Bootstrap] Application failed to load', err);
  const recover = (window as any).__el_recoverBoot;
  if (typeof recover === 'function') recover(err);
}

initializeWhiteLabeling()
  .catch(err => {
      console.error('WL Init rejected unexpectedly, continuing with defaults', err);
      // @ts-ignore
      window.el_wl_loading = false;
      applyBranding(DEFAULT_APP_NAME, 'assets/favicon-16x16.png');
  })
  .then(() => initCacheTagRemotes(nativeInitFederation))
  // Federation init failure is logged but must not block the shell from bootstrapping
  .catch(err => console.error(err))
  .then(_ => import('./bootstrap'))
  // Only the dynamic import failing (e.g. a stale/missing chunk) is a hard boot failure here;
  // a runtime bootstrapApplication() rejection is surfaced from bootstrap.ts instead.
  .catch(recoverFromBootFailure);
